import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, of } from 'rxjs';
import { translate } from '@jsverse/transloco';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { IAddDeviceToRevisionRequest, ICreateRevisionDocumentRequest, IDeleteDeviceToRevisionRequest, IDeleteRevisionDocumentRequest, IGetRevisionResponse, IRevision } from '../../revisions/models/revision';
import { RevisionService } from '../../revisions/services/revision.service';
import { DeviceStore } from './device-store';
import { AppNotificationService } from '../../shared/services/notification.service';

export interface RevisionState {
  revision: IRevision | null;
  loading: boolean;
}

@Injectable()
export class RevisionStore extends ComponentStore<RevisionState> {

  // Initial state
  constructor(
    private store: Store<AppStateInterface>,
    private deviceStore: DeviceStore,
    private revisionService: RevisionService,
    private appnotificationService: AppNotificationService) {
      super({ 
        revision: null, 
        loading: false
      });
  }

  // Selector for the current revision
  readonly selectedRevision$: Observable<IRevision | null> = this.select(state => state.revision);

  // Selector for loading state
  readonly loading$: Observable<boolean> = this.select(state => state.loading);

  readonly updateRevisionDocumentId = this.updater(
    (state, params: { revisionReportDocumentId: string; revisionReportDocumentFileName: string }) => ({
      ...state,
      loading: false,
      errorData: null,
      revision: {
        ...state.revision,
        revisionReportDocumentId: params.revisionReportDocumentId,
        revisionReportDocumentFileName: params.revisionReportDocumentFileName,
      },
    })
  );



  // Effect to load device by ID
  readonly getRevisionById = this.effect<string>(revisionId$ =>
    revisionId$.pipe(
      switchMap(revisionId =>
        this.revisionService.getRevisionById(revisionId).pipe(
          tap(response => {
            var revision : IRevision = {...response};
            this.patchState({revision})
          })
        )
      )
    )
  );

  // Effect to generate revision report pdf
  readonly createRevisionReport = this.effect<ICreateRevisionDocumentRequest>(request$ =>
    request$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(request =>
        this.revisionService.createRevisionReport(request).pipe(
        tap(response => {
          this.updateRevisionDocumentId({
            revisionReportDocumentId: response.revisionReportDocumentId,
            revisionReportDocumentFileName: response.revisionReportDocumentFileName
          });
          this.appnotificationService.notification$.next({
            message: translate('revisionReport.createSuccess'),
            style: 'success'
          });
        }),
          catchError((error) => {
            this.patchState({ loading: false });
            this.appnotificationService.notification$.next({ message:translate('revisionReport.createFailed'), style: 'error' });
            return of(null);
          })
        )
      )
    )
  );

  readonly deleteRevisionReport = this.effect<string>(request$ =>
    request$.pipe(
      switchMap(request =>
        this.revisionService.deleteRevisionReport(request).pipe(
          tap(revision => {
            this.patchState({ revision })
            this.appnotificationService.notification$.next({ message:translate('revisionReport.deleteSuccess'), style: 'success' });
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message:translate('revisionReport.deleteFailed'), style: 'error' });
            return of(null);
          })
        )
      )
    )
  );
  


  // Effect to remove a device from a revision
  readonly removeDeviceFromRevision = this.effect<{ request: IDeleteDeviceToRevisionRequest }>(request$ => 
    request$.pipe(
      switchMap(({ request }) =>
        this.revisionService.deleteDeviceFromRevision( request ).pipe(
          tap(revision => {
            this.appnotificationService.notification$.next({ message:translate('revisions.deleteDeviceFromRevisionSuccess'), style: 'success' });
          }),
          catchError((error) => {
            this.appnotificationService.notification$.next({ message:translate('revisions.deleteDeviceFromRevisionFailed'), style: 'error' });
            return of(null);
          })
        )
      )
    )
  );  
  

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
